/**
 * <jgd-availability> — PDP availability card.
 *
 * Progressive enhancement on top of the server-rendered state:
 * - replaces the "X–Y days" fallback with concrete dates ("Sa. 11.07. – Mi. 15.07.")
 *   computed client-side, so page caching can never serve stale dates,
 * - updates the free-shipping row + progress bar on `cart:update`,
 * - on `variant:update` swaps in its own re-rendered markup for the new
 *   variant: Horizon's variant picker re-fetches the section and hands the
 *   parsed HTML over in `event.detail.data.html` (same pattern as
 *   product-inventory.js / jgd-spec-table.js). Liquid stays the single source
 *   for the stock state — no per-variant data is shipped to the client,
 * - watches the quantity input of the product form and shows a partial-availability
 *   note ("x Stück sofort lieferbar, ab (x+1) Stück in y lieferbar") when the
 *   customer selects more units than are currently in stock and the variant
 *   allows overselling,
 * - keeps the hidden `_customer_delivery_time` line-item property in sync.
 *
 * Text templates come from data attributes ([count]/[amount]/[available]/[threshold]/[days]
 * placeholders), so all copy stays in the locale files.
 */
import { morph } from '@theme/morph';

/** Orders placed at/after this hour (customer's local time) count as placed
 *  the next day for in-stock delivery estimates. Only used client-side —
 *  there's no equivalent in the Liquid render, which doesn't compute dates. */
const ORDER_CUTOFF_HOUR = 11;

class JgdAvailability extends HTMLElement {
  /** @type {AbortController} */
  #controller;

  /** @type {HTMLInputElement|null} */
  #quantityInput = null;

  /** Last known selected quantity, kept in sync via events (not read from the
   *  input directly — the theme's quantity-selector-component updates its own
   *  state and notifies via a `quantity-selector:update` event carrying
   *  `detail.quantity`, rather than firing native input/change events). */
  #quantity = 1;

  /** @type {HTMLElement|null} Reference kept because the row is physically
   *  moved out of this element (see #relocatePartialRow), so it can no
   *  longer be found via this.querySelector(). */
  #partialRow = null;

  connectedCallback() {
    // fresh controller per connect — a reconnected element must re-listen
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    document.addEventListener('cart:update', this.#handleCartUpdate, { signal });
    document.addEventListener('variant:update', this.#handleVariantUpdate, { signal });

    this.#renderDates();
    this.#formatBadgeNumber();
    this.#bindQuantity();
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  get #state() {
    return this.dataset.state;
  }

  /**
   * Facts about the currently rendered (selected) variant, straight from the
   * data attributes Liquid writes for it. Refreshed on every variant switch
   * by the morph in #handleVariantUpdate.
   * @returns {{qty: number, managed: boolean, oversell: boolean, deliveryText: string, herstellerDate: string}}
   */
  get #info() {
    return {
      qty: this.#int('qty'),
      managed: this.dataset.managed === 'true',
      oversell: this.dataset.oversell === 'true',
      deliveryText: this.dataset.deliveryText || '',
      herstellerDate: this.dataset.herstellerDate || '',
    };
  }

  #int(name, fallback = 0) {
    const value = Number(this.dataset[name]);
    return Number.isFinite(value) ? value : fallback;
  }

  get #locale() {
    return this.dataset.locale || document.documentElement.lang || 'de';
  }

  /**
   * Formats cents in the SHOP's money format, derived from the
   * server-rendered sample of 123456 cents (e.g. "€1.234,56") — keeps
   * JS-updated amounts identical to Liquid `| money` output.
   * Falls back to Intl if the sample can't be parsed.
   */
  #formatMoney(cents) {
    const sample = this.dataset.moneySample || '';
    const match = sample.match(/^(.*?)1(\D?)234(\D)56(.*)$/);
    if (match) {
      const [, prefix, thousands, decimal, suffix] = match;
      const whole = Math.floor(cents / 100);
      const fraction = String(Math.round(cents % 100)).padStart(2, '0');
      const grouped = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return `${prefix}${grouped}${decimal}${fraction}${suffix}`;
    }
    return new Intl.NumberFormat(this.#locale, {
      style: 'currency',
      currency: this.dataset.currency || 'EUR',
    }).format(cents / 100);
  }

  #formatCount(count) {
    return new Intl.NumberFormat(this.#locale).format(count);
  }

  /** Day/month/year portion is merchant-configurable via the date_format
   *  block setting; weekday + separator stay fixed. The year-less formats
   *  still show the year once the date falls outside the current year, so a
   *  next-year date can't be mistaken for one that has already passed. */
  #dateFormatOptions(date) {
    const otherYear = date.getFullYear() !== new Date().getFullYear();
    switch (this.dataset.dateFormat) {
      case 'day_month_year':
        return { day: '2-digit', month: '2-digit', year: 'numeric' };
      case 'day_month_long':
        return otherYear ? { day: '2-digit', month: 'long', year: 'numeric' } : { day: '2-digit', month: 'long' };
      case 'day_month_year_long':
        return { day: '2-digit', month: 'long', year: 'numeric' };
      case 'day_month':
      default:
        return otherYear ? { day: '2-digit', month: '2-digit', year: 'numeric' } : { day: '2-digit', month: '2-digit' };
    }
  }

  /** "Sa. 11.07." — assembled from Intl parts so it works for any locale. */
  #formatDate(date) {
    const weekday = new Intl.DateTimeFormat(this.#locale, { weekday: 'short' }).format(date);
    const day = new Intl.DateTimeFormat(this.#locale, this.#dateFormatOptions(date)).format(date);
    const separator = weekday.endsWith('.') ? ' ' : '. ';
    return `${weekday}${separator}${day}`;
  }

  /** Fresh Date instance `days` days from now (today + days). */
  #addDays(days) {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date;
  }

  /** No Sunday deliveries — if a computed date lands on one, push it to
   *  Monday. Only applied to computed date ranges, never to the
   *  merchant-maintained custom.hersteller_lieferdatum date, which is shown
   *  exactly as maintained per explicit request. */
  #skipSunday(date) {
    const result = new Date(date);
    if (result.getDay() === 0) result.setDate(result.getDate() + 1);
    return result;
  }

  /** True if "YYYY-MM-DD" is strictly before today (date-only comparison) or
   *  unparseable. Used to fall back to custom.delivery_time_to_customer / the
   *  computed range once a merchant-maintained hersteller_lieferdatum has
   *  passed — checked fresh on every render rather than trusting a flag
   *  baked in at page-load, so it self-corrects even on a cached page. */
  #isPastDate(dateStr) {
    if (!dateStr) return true;
    const date = new Date(`${dateStr}T00:00:00`);
    if (Number.isNaN(date.getTime())) return true;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  }

  /**
   * Backordered variants (out of stock, Überverkäufe on) show, in priority
   * order: the variant metafield custom.hersteller_lieferdatum (a fixed
   * manufacturer-supplied date) while it's still current; otherwise
   * custom.delivery_time_to_customer as-is; otherwise a computed date range.
   * Same priority as the initial Liquid render, re-applied here so it also
   * holds after a variant switch.
   */
  #renderDates() {
    const range = this.querySelector('[data-delivery-range]');
    if (!range || this.#state === 'sold_out') return;

    const backorder = this.#state === 'backorder';

    if (backorder) {
      const info = this.#info;

      if (info.herstellerDate && !this.#isPastDate(info.herstellerDate)) {
        const date = new Date(`${info.herstellerDate}T00:00:00`);
        range.textContent = this.#formatDate(date);
        const property = this.querySelector('[data-delivery-property]');
        if (property) property.value = range.parentElement.textContent.replace(/\s+/g, ' ').trim();
        return;
      }

      if (info.deliveryText) {
        range.textContent = info.deliveryText;
        const property = this.querySelector('[data-delivery-property]');
        if (property) property.value = range.parentElement.textContent.replace(/\s+/g, ' ').trim();
        return;
      }
    }

    let minDays = this.#int(backorder ? 'backorderMinDays' : 'minDays');
    let maxDays = this.#int(backorder ? 'backorderMaxDays' : 'maxDays', minDays + 1);

    // In-stock order cutoff: an order placed at/after 11:00 (customer's local
    // time) is treated as placed the next day, so the whole delivery window
    // shifts by one day. Doesn't apply to backorder — that's a fixed lead
    // time, not a same-day-dispatch estimate.
    if (!backorder && new Date().getHours() >= ORDER_CUTOFF_HOUR) {
      minDays += 1;
      maxDays += 1;
    }

    // No Sunday deliveries — check each end of the window independently,
    // since only one of them might land on a Sunday.
    const from = this.#skipSunday(this.#addDays(minDays));
    const to = this.#skipSunday(this.#addDays(maxDays));

    const text = `${this.#formatDate(from)} – ${this.#formatDate(to)}`;
    range.textContent = text;

    const property = this.querySelector('[data-delivery-property]');
    if (property) property.value = range.parentElement.textContent.replace(/\s+/g, ' ').trim();
  }

  #formatBadgeNumber() {
    const badge = this.querySelector('[data-badge]');
    if (!badge || badge.classList.contains('hidden')) return;
    badge.textContent = badge.textContent.replace(/\d+/, (n) => this.#formatCount(Number(n)));
  }

  /* ------------------------------ cart state ------------------------------ */

  #handleCartUpdate = () => {
    fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)
      .then((response) => response.json())
      .then((cart) => this.#renderShipping(cart.total_price))
      .catch(() => {});
  };

  #renderShipping(cartTotal) {
    const row = this.querySelector('[data-shipping-row]');
    const threshold = this.#int('threshold');
    if (!row || threshold <= 0) return;

    const text = row.querySelector('[data-shipping-text]');
    const progress = row.querySelector('[data-progress]');
    const fill = row.querySelector('[data-progress-fill]');

    if (cartTotal >= threshold) {
      text.textContent = row.dataset.templateReached;
    } else if (cartTotal > 0) {
      text.innerHTML = row.dataset.templateRemaining.replace('[amount]', this.#formatMoney(threshold - cartTotal));
    } else {
      text.innerHTML = row.dataset.templateFreeFrom.replace('[amount]', this.#formatMoney(threshold));
    }

    if (progress && fill) {
      const percent = Math.min(100, Math.round((cartTotal / threshold) * 100));
      progress.classList.toggle('hidden', cartTotal <= 0);
      progress.setAttribute('aria-valuenow', String(percent));
      fill.style.width = `${percent}%`;
    }
  }

  /* ---------------------------- variant switches --------------------------- */

  /**
   * Horizon's <variant-picker> re-fetches the product section for the new
   * variant and dispatches `variant:update` with the parsed document in
   * `event.detail.data.html`. Our block is part of that section, rendered by
   * Liquid for the new variant (state, status text, badge, delivery row,
   * data attributes) — so we take that markup and morph it in instead of
   * re-deriving the state client-side. If the HTML doesn't contain this
   * block (event without html, different section), the current render stays.
   */
  #handleVariantUpdate = (event) => {
    const detail = event.detail;
    if (String(detail?.data?.productId || '') !== String(this.dataset.productId)) return;

    const updated = detail?.data?.html?.querySelector(`jgd-availability[id="${this.id}"]`);
    if (!updated) return;

    // Attributes first (data-state, data-qty, … are read by the renders
    // below), then the children. The server markup carries a fresh
    // partial-availability row; the previously relocated one (now living in
    // the product form) is dropped so the two never coexist.
    for (const { name, value } of updated.attributes) {
      if (name.startsWith('data-')) this.setAttribute(name, value);
    }
    this.#partialRow?.remove();
    this.#partialRow = null;
    morph(this, updated, { childrenOnly: true });

    const id = detail?.resource?.id;
    if (id != null) this.dataset.currentVariantId = String(id);

    this.#renderDates();
    this.#formatBadgeNumber();
    this.#renderPartial();
  };

  /* ------------------------------ quantity check ---------------------------- */

  #bindQuantity() {
    const formId = this.dataset.formId;
    if (!formId) return;
    const form = document.getElementById(formId);
    if (!form) return;

    this.#quantityInput = form.querySelector('input[name="quantity"]') || null;
    if (this.#quantityInput) {
      this.#quantity = Number(this.#quantityInput.value) || 1;
      // Fallback for plain inputs / manual typing.
      this.#quantityInput.addEventListener('change', this.#handleQuantityChange, { signal: this.#controller.signal });
      this.#quantityInput.addEventListener('input', this.#handleQuantityChange, { signal: this.#controller.signal });
    }

    // The theme's <quantity-selector-component> updates its own state and
    // notifies via this event (bubbles to the form) instead of firing native
    // input/change on the +/- buttons — detail.quantity carries the new value
    // and event.target carries the variant this selector belongs to.
    form.addEventListener('quantity-selector:update', this.#handleQuantityChange, { signal: this.#controller.signal });

    this.#relocatePartialRow(form);
    this.#renderPartial();
  }

  /**
   * The partial-availability row needs to sit right above the quantity
   * selector / add-to-cart button so the customer actually sees it before
   * ordering — wherever on the page this <jgd-availability> block itself is
   * placed. So instead of leaving it inside this component, it's physically
   * moved in the DOM to just before the *container* that holds the quantity
   * selector and the submit button (falling back to just before the submit
   * button's own parent if no quantity selector is found) — NOT before the
   * quantity selector itself, since that container is a flex row pairing
   * quantity + button side by side; inserting a 3rd item directly into it
   * breaks that layout (the button wraps onto its own line). Going one level
   * up keeps that row intact and just adds our note above it. The element
   * keeps its jgd-availability__row / __partial classes, so the existing
   * styling still applies.
   */
  #relocatePartialRow(form) {
    const row = this.querySelector('[data-partial-row]');
    if (!row) return;

    const anchor =
      form.querySelector('quantity-selector-component') ||
      this.#quantityInput?.closest('.quantity-selector') ||
      form.querySelector('button[type="submit"], input[type="submit"]');

    const container = anchor?.parentElement;
    if (container?.parentElement) {
      container.parentElement.insertBefore(row, container);
    } else if (anchor?.parentElement) {
      // No grandparent to anchor before — fall back to the old behavior
      // rather than silently dropping the row.
      anchor.parentElement.insertBefore(row, anchor);
    }

    // Keep a direct reference — once moved, it's no longer inside `this`,
    // so this.querySelector('[data-partial-row]') would return null.
    this.#partialRow = row;
  }

  #handleQuantityChange = (event) => {
    const detailQty = event?.detail?.quantity;
    if (Number.isFinite(detailQty)) {
      this.#quantity = detailQty;
    } else if (this.#quantityInput) {
      this.#quantity = Number(this.#quantityInput.value) || 1;
    }

    const variantId = event?.target?.dataset?.variantId;
    if (variantId) this.dataset.currentVariantId = variantId;

    this.#renderPartial();
  };

  /**
   * Shows "x Stück sofort lieferbar, ab y Stück in [deliveryText] lieferbar"
   * when the selected quantity exceeds current stock and the variant is still
   * purchasable (overselling / Überverkäufe enabled). x is the current stock,
   * y is the quantity the customer actually typed/selected in the quantity
   * field (this.#quantity). deliveryText is free text from the variant
   * metafield (e.g. "3 bis 4 Wochen") and is inserted as-is — it is NOT a
   * number, so it must not go through #formatCount().
   */
  #renderPartial() {
    let row = this.#partialRow;

    // A variant switch morphs the buy-buttons area against the server HTML,
    // which doesn't contain the relocated row — the morph drops it from the
    // DOM and #partialRow goes stale. The same morph re-inserts a fresh row
    // inside this component (it's part of the server markup), so re-run the
    // relocation with that fresh node before rendering.
    if (!row || !row.isConnected) {
      const form = this.dataset.formId ? document.getElementById(this.dataset.formId) : null;
      if (form) this.#relocatePartialRow(form);
      row = this.#partialRow;
    }

    if (!row || !row.isConnected) return;

    const info = this.#info;
    const selected = this.#quantity;
    const availableQty = Math.max(0, info.qty);

    // A purchasable variant isn't enough — what makes ordering beyond current
    // stock possible is info.oversell (inventory_policy == 'continue').
    // availableQty > 0 excludes full backorders (0 in stock) — there's nothing
    // to split there, the top status already covers that case with its own
    // message.
    const show = info.managed && info.oversell && availableQty > 0 && selected > availableQty;
    row.classList.toggle('hidden', !show);
    if (!show) return;

    // threshold is the quantity the customer actually typed/selected — not
    // availableQty + 1 — per explicit request.
    const threshold = selected;
    // info.deliveryText is the raw metafield value (blank if unset) — build
    // the same day-range fallback text Liquid uses when it's empty.
    const days = info.deliveryText || `${this.#int('backorderMinDays')}–${this.#int('backorderMaxDays')} Tagen`;

    const text = row.querySelector('[data-partial-text]');
    if (text) {
      // innerHTML on purpose: the *_html locale value wraps [threshold] and
      // [days] in <strong> for emphasis. The inserted values are plain
      // numbers/merchant-controlled metafield text, not user input.
      text.innerHTML = row.dataset.templatePartial
        .replace('[available]', this.#formatCount(availableQty))
        .replace('[threshold]', this.#formatCount(threshold))
        .replace('[days]', days);
    }
  }
}

if (!customElements.get('jgd-availability')) {
  customElements.define('jgd-availability', JgdAvailability);
}
